import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Users, Wallet, Calculator, FileDown, RefreshCw, Calendar, Search,
  ChevronRight, ChevronDown, Plus, Save, CheckCircle2, AlertCircle,
  Mail, Download, ArrowLeft, TrendingUp,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(2)}%`;

// ────────────────────────────────  Root  ────────────────────────────────
export const CrmPayroll = () => {
  const [tab, setTab] = useState('dashboard');
  const [runDetail, setRunDetail] = useState(null); // { run_id } for detail view

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="crm-payroll-root">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
            Payroll
          </h1>
          <p className="text-sm text-[#6B6B7A]">Ontario/Canada CRA-compliant payroll runs, config &amp; exports.</p>
        </div>
      </div>

      {runDetail ? (
        <RunDetail runId={runDetail} onBack={() => setRunDetail(null)} />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#F1EFE9] p-1 rounded-2xl">
            <TabsTrigger value="dashboard" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="payroll-tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="new" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="payroll-tab-new">New Run</TabsTrigger>
            <TabsTrigger value="history" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="payroll-tab-history">Run History</TabsTrigger>
            <TabsTrigger value="config" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="payroll-tab-config">Configuration</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-4"><Dashboard onOpenRun={(id) => setRunDetail(id)} /></TabsContent>
          <TabsContent value="new" className="mt-4"><NewRunWizard onCreated={(id) => setRunDetail(id)} /></TabsContent>
          <TabsContent value="history" className="mt-4"><History onOpenRun={(id) => setRunDetail(id)} /></TabsContent>
          <TabsContent value="config" className="mt-4"><ConfigTab /></TabsContent>
        </Tabs>
      )}
    </div>
  );
};

// ────────────────────────────────  Dashboard  ────────────────────────────────
const Dashboard = ({ onOpenRun }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/payroll/dashboard`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load dashboard');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="py-12 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>;
  }
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Calculator} color="#FF3B6B" label={`Runs in ${data.tax_year}`} value={data.runs_this_year} testId="payroll-stat-runs" />
        <StatCard icon={Users} color="#2C81E5" label="Employees processed" value={data.employees_processed} testId="payroll-stat-emps" />
        <StatCard icon={TrendingUp} color="#0FAB66" label="Total Gross YTD" value={fmtMoney(data.gross_ytd)} testId="payroll-stat-gross" />
        <StatCard icon={Wallet} color="#7B3FE4" label="Total Net Paid YTD" value={fmtMoney(data.net_ytd)} testId="payroll-stat-net" />
      </div>
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Recent Runs</h3>
          <Button size="sm" variant="outline" onClick={load} className="rounded-full text-xs">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {(data.recent_runs || []).length === 0 ? (
          <p className="text-sm text-[#6B6B7A] italic py-4">No runs yet. Start a New Run from the tab above.</p>
        ) : (
          <ul className="divide-y divide-[#F0EDE6]">
            {data.recent_runs.map((r) => (
              <li key={r.run_id}>
                <button
                  type="button"
                  onClick={() => r.tax_year && onOpenRun(r.run_id)}
                  disabled={!r.tax_year}
                  className={`w-full grid grid-cols-12 items-center gap-3 px-2 py-3 rounded-xl text-left ${r.tax_year ? 'hover:bg-[#FFFDF8]' : 'opacity-60'}`}
                  data-testid={`payroll-recent-${r.run_id}`}
                >
                  <div className="col-span-4">
                    <p className="font-semibold text-sm">{r.period_start} → {r.period_end}</p>
                    <p className="text-[11px] text-[#6B6B7A]">{r.period_type} · run {r.run_id}</p>
                  </div>
                  <div className="col-span-3">
                    <Badge variant="outline" className="text-[10px]">
                      {r.status || (r.generated_count ? `legacy · ${r.generated_count} stubs` : 'legacy')}
                    </Badge>
                  </div>
                  <div className="col-span-3 text-right">
                    <p className="text-[10px] uppercase text-[#6B6B7A]">Gross</p>
                    <p className="font-semibold tabular-nums">{fmtMoney(r?.totals?.gross)}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    {r.tax_year ? <ChevronRight className="w-4 h-4 text-[#6B6B7A] ml-auto" /> : null}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, color, label, value, testId }) => (
  <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <p className="text-[11px] uppercase tracking-wider text-[#6B6B7A]">{label}</p>
    </div>
    <p className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
  </div>
);

// ────────────────────────────────  Config  ────────────────────────────────
const ConfigTab = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/payroll/config/${year}`, { withCredentials: true });
      setCfg(r.data);
    } catch (e) { toast.error('Failed to load config'); }
    finally { setLoading(false); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setBracket = (kind, i, k, v) => setCfg((c) => {
    const arr = [...(c[kind] || [])];
    arr[i] = { ...arr[i], [k]: v };
    return { ...c, [kind]: arr };
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      delete payload.config_id;
      delete payload.tax_year;
      delete payload.created_at;
      delete payload.updated_at;
      // Coerce numeric strings
      Object.keys(payload).forEach((k) => {
        if (typeof payload[k] === 'string' && !isNaN(Number(payload[k]))) {
          payload[k] = Number(payload[k]);
        }
      });
      await axios.put(`${API}/api/admin/payroll/config/${year}`, payload, { withCredentials: true });
      toast.success(`Payroll config for ${year} saved`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save config');
    } finally { setSaving(false); }
  };

  if (loading || !cfg) return <div className="py-12 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium">Tax Year:</label>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="h-9 rounded-xl border border-[#E8E5DF] px-3 text-sm bg-white"
          data-testid="payroll-config-year"
        >
          {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map((y) =>
            <option key={y} value={y}>{y}</option>)}
        </select>
        <Button
          onClick={save} disabled={saving}
          className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs ml-auto"
          data-testid="payroll-config-save"
        >
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save Configuration'}
        </Button>
      </div>

      <ConfigSection title="CPP (Canada Pension Plan — Base)" color="#FF3B6B">
        <Field label="Contribution Rate (%)" value={cfg.cpp_rate * 100} onChange={(v) => setField('cpp_rate', Number(v) / 100)} testId="cfg-cpp-rate" />
        <Field label="YMPE (Year's Max Pensionable)" value={cfg.cpp_ympe} onChange={(v) => setField('cpp_ympe', Number(v))} testId="cfg-cpp-ympe" prefix="$" />
        <Field label="Basic Exemption (YBE)" value={cfg.cpp_basic_exemption} onChange={(v) => setField('cpp_basic_exemption', Number(v))} testId="cfg-cpp-ybe" prefix="$" />
        <ReadOnly label="Max Annual Contribution" value={fmtMoney(cfg.cpp_max_contrib)} />
      </ConfigSection>

      <ConfigSection title="CPP2 (Second Additional CPP)" color="#7B3FE4">
        <Field label="Contribution Rate (%)" value={cfg.cpp2_rate * 100} onChange={(v) => setField('cpp2_rate', Number(v) / 100)} testId="cfg-cpp2-rate" />
        <Field label="YAMPE (Year's Additional Max)" value={cfg.cpp2_yampe} onChange={(v) => setField('cpp2_yampe', Number(v))} testId="cfg-cpp2-yampe" prefix="$" />
        <ReadOnly label="Max Annual CPP2 Contribution" value={fmtMoney(cfg.cpp2_max_contrib)} />
      </ConfigSection>

      <ConfigSection title="EI (Employment Insurance)" color="#0FAB66">
        <Field label="Premium Rate (%)" value={cfg.ei_rate * 100} onChange={(v) => setField('ei_rate', Number(v) / 100)} testId="cfg-ei-rate" />
        <Field label="Max Insurable Earnings" value={cfg.ei_mie} onChange={(v) => setField('ei_mie', Number(v))} testId="cfg-ei-mie" prefix="$" />
        <ReadOnly label="Max Annual EI Contribution" value={fmtMoney(cfg.ei_max_contrib)} />
      </ConfigSection>

      <ConfigSection title="Federal Income Tax" color="#2C81E5">
        <div className="col-span-full">
          <p className="text-[11px] uppercase text-[#6B6B7A] mb-1">Tax Brackets</p>
          <div className="space-y-2">
            {(cfg.fed_brackets || []).map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-[#6B6B7A] w-16">Up to</span>
                <Input type="number" value={b.upper} onChange={(e) => setBracket('fed_brackets', i, 'upper', Number(e.target.value))} className="h-8 rounded-lg border-[#E8E5DF] text-sm max-w-[180px]" />
                <span className="text-xs text-[#6B6B7A]">→</span>
                <Input type="number" step="0.001" value={b.rate * 100} onChange={(e) => setBracket('fed_brackets', i, 'rate', Number(e.target.value) / 100)} className="h-8 rounded-lg border-[#E8E5DF] text-sm max-w-[100px]" />
                <span className="text-xs text-[#6B6B7A]">%</span>
              </div>
            ))}
          </div>
        </div>
        <Field label="Basic Personal Amount (BPA base)" value={cfg.fed_bpa_base} onChange={(v) => setField('fed_bpa_base', Number(v))} prefix="$" />
        <Field label="BPA Additional (high income phase-out)" value={cfg.fed_bpa_add} onChange={(v) => setField('fed_bpa_add', Number(v))} prefix="$" />
        <Field label="Phase-out Start" value={cfg.fed_bpa_phaseout_start} onChange={(v) => setField('fed_bpa_phaseout_start', Number(v))} prefix="$" />
        <Field label="Phase-out End" value={cfg.fed_bpa_phaseout_end} onChange={(v) => setField('fed_bpa_phaseout_end', Number(v))} prefix="$" />
      </ConfigSection>

      <ConfigSection title="Ontario Provincial Tax" color="#FFA02C">
        <div className="col-span-full">
          <p className="text-[11px] uppercase text-[#6B6B7A] mb-1">Tax Brackets</p>
          <div className="space-y-2">
            {(cfg.on_brackets || []).map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-[#6B6B7A] w-16">Up to</span>
                <Input type="number" value={b.upper} onChange={(e) => setBracket('on_brackets', i, 'upper', Number(e.target.value))} className="h-8 rounded-lg border-[#E8E5DF] text-sm max-w-[180px]" />
                <span className="text-xs text-[#6B6B7A]">→</span>
                <Input type="number" step="0.001" value={b.rate * 100} onChange={(e) => setBracket('on_brackets', i, 'rate', Number(e.target.value) / 100)} className="h-8 rounded-lg border-[#E8E5DF] text-sm max-w-[100px]" />
                <span className="text-xs text-[#6B6B7A]">%</span>
              </div>
            ))}
          </div>
        </div>
        <Field label="Basic Personal Amount (BPA)" value={cfg.on_bpa} onChange={(v) => setField('on_bpa', Number(v))} prefix="$" />
        <Field label="Surtax Tier 1 Threshold" value={cfg.on_surtax_tier1_threshold} onChange={(v) => setField('on_surtax_tier1_threshold', Number(v))} prefix="$" />
        <Field label="Surtax Tier 2 Threshold" value={cfg.on_surtax_tier2_threshold} onChange={(v) => setField('on_surtax_tier2_threshold', Number(v))} prefix="$" />
      </ConfigSection>

      <ConfigSection title="Overtime Rules" color="#9333EA">
        <Field label="Overtime Multiplier (e.g. 1.5)" value={cfg.overtime_multiplier} onChange={(v) => setField('overtime_multiplier', Number(v))} />
        <Field label="Weekly Threshold (hours)" value={cfg.overtime_threshold_hours_per_week} onChange={(v) => setField('overtime_threshold_hours_per_week', Number(v))} />
      </ConfigSection>
    </div>
  );
};

const ConfigSection = ({ title, color, children }) => (
  <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
    <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color }}>{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, prefix, testId }) => (
  <div>
    <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">{label}</label>
    <div className="flex items-center gap-1">
      {prefix && <span className="text-sm text-[#6B6B7A]">{prefix}</span>}
      <Input type="number" step="any" value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid={testId} />
    </div>
  </div>
);

const ReadOnly = ({ label, value }) => (
  <div>
    <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">{label}</label>
    <div className="h-9 rounded-xl border border-dashed border-[#E8E5DF] px-3 flex items-center text-sm text-[#6B6B7A] bg-[#FAF8F3] tabular-nums">
      {value} <span className="ml-auto text-[10px] uppercase">auto</span>
    </div>
  </div>
);

// ────────────────────────────────  New Run Wizard  ────────────────────────────────
const NewRunWizard = ({ onCreated }) => {
  const [step, setStep] = useState(1);
  const today = new Date().toISOString().slice(0, 10);
  const [periodType, setPeriodType] = useState('biweekly');
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 14);
    return d.toISOString().slice(0, 10);
  });
  const [periodEnd, setPeriodEnd] = useState(today);
  const [employees, setEmployees] = useState([]);
  const [selectedIds, setSelectedIds] = useState({});
  const [overrides, setOverrides] = useState({}); // { emp_id: { regular_hours, overtime_hours } }
  const [preview, setPreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const [emailAfter, setEmailAfter] = useState(false);
  const [empLoading, setEmpLoading] = useState(false);

  // Load employees on mount
  useEffect(() => {
    (async () => {
      setEmpLoading(true);
      try {
        const r = await axios.get(`${API}/api/admin/payroll/employees`, { withCredentials: true });
        setEmployees(r.data.employees || []);
        // Preselect all
        const sel = {};
        (r.data.employees || []).forEach((e) => { sel[e.user_id] = true; });
        setSelectedIds(sel);
      } catch (e) { toast.error('Failed to load employees'); }
      finally { setEmpLoading(false); }
    })();
  }, []);

  const selectedList = useMemo(() =>
    employees.filter((e) => selectedIds[e.user_id]), [employees, selectedIds]);

  const runPreview = async () => {
    if (selectedList.length === 0) { toast.error('Select at least one employee'); return; }
    try {
      const r = await axios.post(`${API}/api/admin/payroll/runs/preview`, {
        period_start: periodStart, period_end: periodEnd, period_type: periodType,
        employee_ids: selectedList.map((e) => e.user_id),
        hours_overrides: overrides,
      }, { withCredentials: true });
      setPreview(r.data);
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
  };

  const finalize = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/api/admin/payroll/runs`, {
        period_start: periodStart, period_end: periodEnd, period_type: periodType,
        employee_ids: selectedList.map((e) => e.user_id),
        hours_overrides: overrides,
        finalize: true,
        email_paystubs: emailAfter,
      }, { withCredentials: true });
      toast.success(`Payroll run ${r.data.run.run_id} finalized · ${r.data.run.employees_count} paystubs generated`);
      if (emailAfter) {
        toast.success(`${r.data.email_results?.sent || 0} paystubs emailed`);
      }
      onCreated(r.data.run.run_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create run'); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-4">
      {/* Stepper */}
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step >= s ? 'bg-[#FF3B6B] text-white' : 'bg-[#F1EFE9] text-[#6B6B7A]'}`}
              data-testid={`payroll-wizard-step-${s}`}>{s}</div>
            <div className="text-xs">
              {['Period', 'Employees', 'Review', 'Confirm'][s - 1]}
            </div>
            {s < 4 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-[#FF3B6B]' : 'bg-[#F1EFE9]'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Period */}
      {step === 1 && (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-3" data-testid="payroll-step-period">
          <h3 className="font-bold" style={{ fontFamily: 'Fredoka' }}>Step 1 — Select Pay Period</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">Period Type</label>
              <select value={periodType} onChange={(e) => setPeriodType(e.target.value)}
                className="h-9 rounded-xl border border-[#E8E5DF] px-3 text-sm bg-white w-full"
                data-testid="payroll-period-type">
                <option value="weekly">Weekly (52/yr)</option>
                <option value="biweekly">Bi-weekly (26/yr)</option>
                <option value="semi_monthly">Semi-monthly (24/yr)</option>
                <option value="monthly">Monthly (12/yr)</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">Period Start</label>
              <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
                className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="payroll-period-start" />
            </div>
            <div>
              <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">Period End</label>
              <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
                className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="payroll-period-end" />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={() => setStep(2)} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="payroll-wizard-next-1">
              Next: Select Employees →
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Employees */}
      {step === 2 && (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-3" data-testid="payroll-step-employees">
          <div className="flex items-center justify-between">
            <h3 className="font-bold" style={{ fontFamily: 'Fredoka' }}>Step 2 — Select Employees ({selectedList.length}/{employees.length})</h3>
            <div className="flex gap-2 text-xs">
              <button type="button" onClick={() => {
                const sel = {}; employees.forEach((e) => { sel[e.user_id] = true; }); setSelectedIds(sel);
              }} className="underline text-[#FF3B6B]" data-testid="payroll-select-all">Select All</button>
              <button type="button" onClick={() => setSelectedIds({})} className="underline text-[#6B6B7A]">Clear</button>
            </div>
          </div>
          {empLoading ? (
            <div className="py-8 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin" /></div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto border border-[#E8E5DF] rounded-xl">
              <table className="w-full text-sm">
                <thead className="bg-[#FFFDF8] sticky top-0"><tr>
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-[#6B6B7A]">Employee</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-[#6B6B7A]">Role</th>
                  <th className="px-3 py-2 text-right text-[11px] uppercase text-[#6B6B7A]">Rate/hr</th>
                </tr></thead>
                <tbody>
                  {employees.map((e) => (
                    <tr key={e.user_id} className="border-t border-[#F0EDE6] hover:bg-[#FFFDF8]">
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={!!selectedIds[e.user_id]}
                          onCheckedChange={(c) => setSelectedIds((s) => ({ ...s, [e.user_id]: !!c }))}
                          data-testid={`payroll-emp-check-${e.user_id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <p className="font-medium">{e.name}</p>
                        <p className="text-[11px] text-[#6B6B7A]">{e.email}</p>
                      </td>
                      <td className="px-3 py-2 text-xs">{e.employee_role || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(e.hourly_rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => setStep(1)} className="rounded-full">← Back</Button>
            <Button onClick={runPreview} disabled={selectedList.length === 0}
              className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
              data-testid="payroll-wizard-preview">
              Preview Calculations →
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Review */}
      {step === 3 && preview && (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-3" data-testid="payroll-step-review">
          <h3 className="font-bold" style={{ fontFamily: 'Fredoka' }}>Step 3 — Review Hours &amp; Deductions</h3>
          <TotalsStrip totals={preview.totals} count={preview.employee_count} />
          <div className="overflow-x-auto max-h-[500px] border border-[#E8E5DF] rounded-xl">
            <table className="w-full text-xs">
              <thead className="bg-[#FFFDF8] sticky top-0 text-[10px] uppercase text-[#6B6B7A]"><tr>
                <th className="px-2 py-2 text-left">Employee</th>
                <th className="px-2 py-2 text-right">Regular hrs</th>
                <th className="px-2 py-2 text-right">OT hrs</th>
                <th className="px-2 py-2 text-right">Gross</th>
                <th className="px-2 py-2 text-right">CPP</th>
                <th className="px-2 py-2 text-right">EI</th>
                <th className="px-2 py-2 text-right">Fed Tax</th>
                <th className="px-2 py-2 text-right">Prov Tax</th>
                <th className="px-2 py-2 text-right">Net</th>
              </tr></thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.employee_id} className="border-t border-[#F0EDE6]" data-testid={`payroll-review-row-${r.employee_id}`}>
                    <td className="px-2 py-2">
                      <p className="font-medium">{r.employee_name}</p>
                      <p className="text-[10px] text-[#6B6B7A]">{r.employee_role}</p>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Input type="number" step="0.25" value={overrides[r.employee_id]?.regular_hours ?? r.regular_hours}
                        onChange={(e) => setOverrides((o) => ({ ...o, [r.employee_id]: { ...o[r.employee_id], regular_hours: Number(e.target.value) } }))}
                        className="h-7 rounded-lg border-[#E8E5DF] text-xs w-20 text-right"
                        data-testid={`payroll-review-reg-${r.employee_id}`} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Input type="number" step="0.25" value={overrides[r.employee_id]?.overtime_hours ?? r.overtime_hours}
                        onChange={(e) => setOverrides((o) => ({ ...o, [r.employee_id]: { ...o[r.employee_id], overtime_hours: Number(e.target.value) } }))}
                        className="h-7 rounded-lg border-[#E8E5DF] text-xs w-20 text-right" />
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums font-semibold">{fmtMoney(r.gross_pay)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-[#D4302A]">-{fmtMoney(r.cpp + r.cpp2)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-[#D4302A]">-{fmtMoney(r.ei)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-[#D4302A]">-{fmtMoney(r.federal_tax)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-[#D4302A]">-{fmtMoney(r.provincial_tax)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-bold text-[#0FAB66]">{fmtMoney(r.net_pay)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center pt-2">
            <Button variant="outline" onClick={() => setStep(2)} className="rounded-full">← Back to Employees</Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={runPreview} className="rounded-full text-xs" data-testid="payroll-review-recalc">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Recalculate
              </Button>
              <Button onClick={() => setStep(4)} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="payroll-wizard-next-3">
                Next: Confirm →
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Confirm */}
      {step === 4 && preview && (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-4" data-testid="payroll-step-confirm">
          <h3 className="font-bold" style={{ fontFamily: 'Fredoka' }}>Step 4 — Confirm &amp; Finalize</h3>
          <div className="p-4 rounded-xl bg-[#FFEDF3] border border-[#FF3B6B] text-sm space-y-1">
            <p><strong>Period:</strong> {periodStart} → {periodEnd} ({periodType})</p>
            <p><strong>Employees:</strong> {preview.employee_count}</p>
            <p><strong>Total Gross:</strong> {fmtMoney(preview.totals.gross)}</p>
            <p><strong>Total Deductions:</strong> {fmtMoney(preview.totals.deductions)}</p>
            <p><strong>Total Net Pay:</strong> <span className="font-bold text-[#0FAB66]">{fmtMoney(preview.totals.net)}</span></p>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={emailAfter} onCheckedChange={(v) => setEmailAfter(!!v)} data-testid="payroll-confirm-email" />
            <label className="text-sm cursor-pointer" onClick={() => setEmailAfter((v) => !v)}>
              Email paystubs to employees immediately after finalization
            </label>
          </div>
          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => setStep(3)} className="rounded-full">← Back to Review</Button>
            <Button onClick={finalize} disabled={creating}
              className="rounded-full bg-[#0FAB66] hover:bg-[#0d9257] text-white"
              data-testid="payroll-wizard-finalize">
              <CheckCircle2 className="w-4 h-4 mr-1" /> {creating ? 'Creating…' : 'Create Payroll Run'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const TotalsStrip = ({ totals, count }) => (
  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
    <div className="rounded-xl bg-[#FFFDF8] border border-[#E8E5DF] p-3">
      <p className="text-[10px] uppercase text-[#6B6B7A]">Employees</p>
      <p className="text-xl font-bold">{count}</p>
    </div>
    <div className="rounded-xl bg-[#E5F8EE] border border-[#0FAB66] p-3">
      <p className="text-[10px] uppercase text-[#6B6B7A]">Gross</p>
      <p className="text-xl font-bold tabular-nums">{fmtMoney(totals.gross)}</p>
    </div>
    <div className="rounded-xl bg-[#FFEDF3] border border-[#FF3B6B] p-3">
      <p className="text-[10px] uppercase text-[#6B6B7A]">CPP + EI</p>
      <p className="text-xl font-bold tabular-nums">{fmtMoney((totals.cpp || 0) + (totals.ei || 0))}</p>
    </div>
    <div className="rounded-xl bg-[#FFF4E0] border border-[#FFA02C] p-3">
      <p className="text-[10px] uppercase text-[#6B6B7A]">Taxes</p>
      <p className="text-xl font-bold tabular-nums">{fmtMoney((totals.federal_tax || 0) + (totals.provincial_tax || 0))}</p>
    </div>
    <div className="rounded-xl bg-[#F0E5FE] border border-[#7B3FE4] p-3">
      <p className="text-[10px] uppercase text-[#6B6B7A]">Net Pay</p>
      <p className="text-xl font-bold tabular-nums text-[#0FAB66]">{fmtMoney(totals.net)}</p>
    </div>
  </div>
);

// ────────────────────────────────  History  ────────────────────────────────
const History = ({ onOpenRun }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      const r = await axios.get(`${API}/api/admin/payroll/runs`, { params, withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error('Failed to load runs'); }
    finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <label className="text-xs uppercase text-[#6B6B7A]">Status:</label>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="h-8 rounded-lg border border-[#E8E5DF] px-2 text-sm bg-white" data-testid="payroll-history-filter">
          <option value="all">All</option>
          <option value="finalized">Finalized</option>
          <option value="draft">Draft</option>
        </select>
        <Button size="sm" variant="outline" onClick={load} className="rounded-full text-xs ml-auto">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFDF8]"><tr className="text-left text-[11px] uppercase text-[#6B6B7A]">
            <th className="px-4 py-2">Run ID</th>
            <th className="px-4 py-2">Period</th>
            <th className="px-4 py-2">Type</th>
            <th className="px-4 py-2">Employees</th>
            <th className="px-4 py-2 text-right">Gross</th>
            <th className="px-4 py-2 text-right">Deductions</th>
            <th className="px-4 py-2 text-right">Net</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2"></th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-[#6B6B7A]"><RefreshCw className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : (data?.rows || []).length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-[#6B6B7A]" data-testid="payroll-history-empty">No payroll runs found.</td></tr>
            ) : data.rows.map((r) => (
              <tr key={r.run_id} className="border-t border-[#F0EDE6] hover:bg-[#FFFDF8]" data-testid={`payroll-history-row-${r.run_id}`}>
                <td className="px-4 py-2 font-mono text-xs">{r.run_id}</td>
                <td className="px-4 py-2 text-xs whitespace-nowrap">{r.period_start} → {r.period_end}</td>
                <td className="px-4 py-2 text-xs">{r.period_type}</td>
                <td className="px-4 py-2 text-xs">{r.employees_count}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(r?.totals?.gross)}</td>
                <td className="px-4 py-2 text-right tabular-nums text-[#D4302A]">-{fmtMoney(r?.totals?.deductions)}</td>
                <td className="px-4 py-2 text-right tabular-nums text-[#0FAB66] font-semibold">{fmtMoney(r?.totals?.net)}</td>
                <td className="px-4 py-2"><Badge variant="outline" className="text-[10px]">{r.status}</Badge></td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => onOpenRun(r.run_id)} className="text-xs text-[#FF3B6B] hover:underline">
                    Open →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ────────────────────────────────  Run Detail  ────────────────────────────────
const RunDetail = ({ runId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/payroll/runs/${runId}`, { withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error('Failed to load run'); }
    finally { setLoading(false); }
  }, [runId]);
  useEffect(() => { load(); }, [load]);

  const emailAll = async () => {
    setEmailing(true);
    try {
      const r = await axios.post(`${API}/api/admin/payroll/runs/${runId}/email-all`, { only_unsent: true }, { withCredentials: true });
      toast.success(`Sent ${r.data.sent} paystub(s). ${r.data.failed?.length || 0} failed.`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk email failed'); }
    finally { setEmailing(false); }
  };

  const downloadBlob = async (url, filename) => {
    try {
      const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([res.data]);
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) { toast.error('Download failed'); }
  };

  if (loading || !data) return <div className="py-12 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>;
  const { run, paystubs } = data;

  return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={onBack} className="text-sm text-[#FF3B6B] -ml-2" data-testid="payroll-runs-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Payroll
      </Button>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Fredoka' }}>Payroll Run {run.run_id}</h2>
            <p className="text-sm text-[#6B6B7A]">
              {run.period_start} → {run.period_end} · {run.period_type} · {run.employees_count} employees
            </p>
            <Badge variant="outline" className="mt-1 text-[10px]">{run.status}</Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={emailAll} disabled={emailing}
              className="rounded-full text-xs" data-testid="payroll-detail-email-all">
              <Mail className="w-3.5 h-3.5 mr-1" /> {emailing ? 'Sending…' : 'Email Unsent'}
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => downloadBlob(`${API}/api/admin/payroll/runs/${runId}/cra-export.csv`, `cra_export_${runId}.csv`)}
              className="rounded-full text-xs" data-testid="payroll-detail-cra-csv">
              <FileDown className="w-3.5 h-3.5 mr-1" /> CRA Export CSV
            </Button>
            <Button size="sm"
              onClick={() => downloadBlob(`${API}/api/admin/payroll/runs/${runId}/summary.pdf`, `payroll_summary_${runId}.pdf`)}
              className="rounded-full text-xs bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="payroll-detail-summary-pdf">
              <Download className="w-3.5 h-3.5 mr-1" /> Summary PDF
            </Button>
          </div>
        </div>
        <TotalsStrip totals={run.totals} count={run.employees_count} />
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
        <h3 className="font-bold mb-3" style={{ fontFamily: 'Fredoka' }}>Paystubs ({paystubs.length})</h3>
        <div className="overflow-x-auto border border-[#E8E5DF] rounded-xl">
          <table className="w-full text-xs">
            <thead className="bg-[#FFFDF8] text-[10px] uppercase text-[#6B6B7A]"><tr>
              <th className="px-2 py-2 text-left">Employee</th>
              <th className="px-2 py-2 text-right">Hours</th>
              <th className="px-2 py-2 text-right">Gross</th>
              <th className="px-2 py-2 text-right">CPP</th>
              <th className="px-2 py-2 text-right">EI</th>
              <th className="px-2 py-2 text-right">Fed</th>
              <th className="px-2 py-2 text-right">Prov</th>
              <th className="px-2 py-2 text-right">Net</th>
              <th className="px-2 py-2">Email</th>
              <th className="px-2 py-2"></th>
            </tr></thead>
            <tbody>
              {paystubs.map((p) => (
                <tr key={p.paystub_id} className="border-t border-[#F0EDE6]" data-testid={`payroll-paystub-${p.paystub_id}`}>
                  <td className="px-2 py-2">
                    <p className="font-medium">{p.employee_name}</p>
                    <p className="text-[10px] text-[#6B6B7A]">{p.employee_email}</p>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">{Number(p.total_hours || 0).toFixed(2)}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-semibold">{fmtMoney(p.gross_pay)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-[#D4302A]">-{fmtMoney((p.cpp || 0) + (p.cpp2 || 0))}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-[#D4302A]">-{fmtMoney(p.ei)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-[#D4302A]">-{fmtMoney(p.federal_tax)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-[#D4302A]">-{fmtMoney(p.provincial_tax)}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-bold text-[#0FAB66]">{fmtMoney(p.net_pay)}</td>
                  <td className="px-2 py-2">
                    {p.email_sent_at ? (
                      <Badge variant="outline" className="text-[9px] bg-[#E5F8EE] border-[#0FAB66] text-[#0FAB66]">Sent</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[9px]">Pending</Badge>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <Button size="sm" variant="outline" className="rounded-full text-[10px] h-7 px-2"
                      onClick={() => downloadBlob(`${API}/api/admin/paystubs/${p.paystub_id}/pdf`, `paystub_${p.paystub_id}.pdf`)}
                      data-testid={`payroll-paystub-pdf-${p.paystub_id}`}>
                      <FileDown className="w-3 h-3 mr-0.5" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CrmPayroll;
